import React from 'react';
import { motion } from 'framer-motion';
import { Star, Users, Globe, Award, Heart, TrendingUp } from 'lucide-react';

interface StatItem {
  icon: React.ComponentType<any>;
  value: string;
  label: string;
  color: string;
}

const stats: StatItem[] = [
  {
    icon: Users,
    value: "50,000+",
    label: "Women Trust Us",
    color: "from-pink-500 to-rose-500"
  },
  {
    icon: Star,
    value: "4.9/5",
    label: "App Store Rating",
    color: "from-yellow-400 to-orange-500"
  },
  {
    icon: Globe,
    value: "120+",
    label: "Countries Worldwide",
    color: "from-blue-500 to-cyan-500"
  },
  {
    icon: Award,
    value: "98%",
    label: "Prediction Accuracy",
    color: "from-purple-500 to-indigo-500"
  }
];

const recentActivity = [
  { name: "Sarah from NYC", action: "started tracking", time: "2 minutes ago", avatar: "👩🏻‍💼" },
  { name: "Maria from Madrid", action: "got her period prediction", time: "5 minutes ago", avatar: "👩🏻‍🏫" },
  { name: "Priya from Mumbai", action: "shared with her doctor", time: "8 minutes ago", avatar: "👩🏽‍⚕️" },
  { name: "Emma from London", action: "upgraded to premium", time: "12 minutes ago", avatar: "👩🏼‍🎓" },
  { name: "Aisha from Lagos", action: "recommended to friends", time: "15 minutes ago", avatar: "👩🏿‍💻" }
];

const SocialProof: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Stats Section */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/30 hover:bg-white/80 transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5, scale: 1.02 }}
          >
            <motion.div
              className={`w-12 h-12 mx-auto mb-4 bg-gradient-to-br ${stat.color} rounded-full flex items-center justify-center`}
              whileHover={{ rotate: 10, scale: 1.1 }}
            >
              <stat.icon className="w-6 h-6 text-white" />
            </motion.div>
            <motion.div
              className="text-2xl font-bold text-gray-900 mb-1"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 + index * 0.1, type: "spring", stiffness: 200 }}
            >
              {stat.value}
            </motion.div>
            <p className="text-sm text-gray-600">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Live Activity Feed */}
      <motion.div
        className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <motion.div
              className="w-3 h-3 bg-green-500 rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <h3 className="font-semibold text-gray-900">Live Activity</h3>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <TrendingUp className="w-4 h-4" />
            <span>Real-time updates</span>
          </div>
        </div>

        <div className="space-y-3">
          {recentActivity.map((activity, index) => (
            <motion.div
              key={index}
              className="flex items-center space-x-3 p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-white/30"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + index * 0.1 }}
              whileHover={{ x: 4 }}
            >
              <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-sm">
                {activity.avatar}
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">{activity.name}</span> {activity.action}
                </p>
                <p className="text-xs text-gray-500">{activity.time}</p>
              </div>
              <Heart className="w-4 h-4 text-pink-400" />
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-4 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <p className="text-sm text-purple-600 font-medium">
            Join the community of empowered women! 💜
          </p>
        </motion.div>
      </motion.div>

      {/* Trust Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          whileHover={{ y: -5 }}
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
            <Award className="w-6 h-6 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Medically Reviewed</h3>
          <p className="text-sm text-gray-600">Approved by gynecologists and reproductive health experts</p>
        </motion.div>

        <motion.div
          className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          whileHover={{ y: -5 }}
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Community Driven</h3>
          <p className="text-sm text-gray-600">Built with feedback from thousands of women worldwide</p>
        </motion.div>

        <motion.div
          className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          whileHover={{ y: -5 }}
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Women-First Design</h3>
          <p className="text-sm text-gray-600">Created by women, for women, with empathy at every step</p>
        </motion.div>
      </div>
    </div>
  );
};

export default SocialProof;